import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeesApi, transfersApi, locationsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Users,
  MapPin,
  AlertCircle,
  User,
  Eye,
  Loader2,
  UserX,
  RotateCcw,
  ArrowRightLeft,
  Check,
  X,
  ShieldCheck,
  RefreshCw,
  KeyRound,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const Employees = () => {
  const { user, selectedLocation, locations, isAdmin, isAccountingAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [formerEmployees, setFormerEmployees] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [allLocations, setAllLocations] = useState([]); // All locations for transfer dropdown
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showTerminateConfirm, setShowTerminateConfirm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(null);
  const [showReinstateModal, setShowReinstateModal] = useState(null);
  const [showPromoteModal, setShowPromoteModal] = useState(null);
  const [showResetPasscode, setShowResetPasscode] = useState(null);
  const [resetPasscodeValue, setResetPasscodeValue] = useState('');
  const [resetPasscodeConfirm, setResetPasscodeConfirm] = useState('');
  const [resetPasscodeReason, setResetPasscodeReason] = useState('');
  const [resettingPasscode, setResettingPasscode] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [employeeFilter, setEmployeeFilter] = useState('active'); // active, former, or transfers
  
  // Termination form state
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationDate, setTerminationDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [terminationNotes, setTerminationNotes] = useState('');
  const [customTerminationReason, setCustomTerminationReason] = useState('');
  
  // Transfer form state
  const [transferDestination, setTransferDestination] = useState('');
  const [transferReason, setTransferReason] = useState('');
  
  // Reinstatement form state
  const [reinstatementReason, setReinstatementReason] = useState('');

  // Promote form state
  const [promoteRole, setPromoteRole] = useState('manager');
  const [promotePassword, setPromotePassword] = useState('');
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteManagedLocations, setPromoteManagedLocations] = useState([]);

  const hasAdminRole = user?.role === 'admin' || user?.role === 'accounting_admin' || 
                       (user?.roles || []).some(r => ['admin', 'accounting_admin'].includes(r)) ||
                       (isAdmin && isAdmin());
  
  // Check if user is accounting admin (for viewing manager pay rates)
  const canViewManagerPay = isAccountingAdmin ? isAccountingAdmin() : false;
  
  // Check if user is manager (not assistant manager) for terminate/transfer permissions
  const isManagerRole = user?.role === 'manager' || (user?.roles || []).includes('manager');
  const isAssistantManagerOnly = (user?.role === 'assistant_manager' || (user?.roles || []).includes('assistant_manager')) && !isManagerRole && !hasAdminRole;

  const [newEmployee, setNewEmployee] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    hourly_rate: '',
    location_id: selectedLocation !== 'all' ? selectedLocation : '',
    password: 'emp123',
    date_of_birth: '',
    address: '',
    hire_date: '',
    start_date: '',
    notes: '',
    emergency_contact: {
      name: '',
      relationship: '',
      phone: '',
    },
  });

  useEffect(() => {
    loadData();
    loadAllLocations();
  }, [selectedLocation, employeeFilter]);

  const loadAllLocations = async () => {
    try {
      const locs = await locationsApi.getAllForTransfer();
      setAllLocations(locs);
    } catch (error) {
      console.error('Failed to load all locations:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (employeeFilter === 'former') {
        const data = await employeesApi.getFormer(selectedLocation);
        setFormerEmployees(data);
      } else if (employeeFilter === 'transfers') {
        const data = await transfersApi.getAll();
        setTransfers(data);
      } else {
        const data = await employeesApi.getAll(selectedLocation);
        // Filter out terminated employees
        setEmployees(data.filter(e => !e.is_terminated && e.status !== 'terminated'));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = loadData; // Alias for backward compatibility

  const filteredEmployees = useMemo(() => {
    const list = employeeFilter === 'former' ? formerEmployees : employees;
    return list.filter((emp) => {
      const matchesSearch =
        emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (emp.employee_number || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [employees, formerEmployees, searchQuery, employeeFilter]);

  const handleAddEmployee = async () => {
    try {
      const employeeData = {
        ...newEmployee,
        hourly_rate: parseFloat(newEmployee.hourly_rate) || 0,
        employee_number: '', // Will be auto-generated
      };
      
      await employeesApi.create(employeeData);
      await loadEmployees();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add employee:', error);
      alert('Failed to add employee: ' + error.message);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee) return;
    
    try {
      const updateData = {
        name: editingEmployee.name,
        email: editingEmployee.email,
        phone: editingEmployee.phone,
        position: editingEmployee.position,
        hourly_rate: parseFloat(editingEmployee.hourly_rate) || 0,
        date_of_birth: editingEmployee.date_of_birth,
        address: editingEmployee.address,
        notes: editingEmployee.notes,
        emergency_contact: editingEmployee.emergency_contact,
      };
      
      await employeesApi.update(editingEmployee.id, updateData);
      await loadEmployees();
      setEditingEmployee(null);
    } catch (error) {
      console.error('Failed to update employee:', error);
      alert('Failed to update employee: ' + error.message);
    }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      await employeesApi.delete(id);
      await loadEmployees();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete employee:', error);
      alert('Failed to delete employee: ' + error.message);
    }
  };

  const handleTerminateEmployee = async (emp) => {
    if (!terminationReason.trim()) {
      alert('Please select a termination reason');
      return;
    }
    // If "Other" is selected, require custom reason
    if (terminationReason === 'Other' && !customTerminationReason.trim()) {
      alert('Please provide a custom termination reason');
      return;
    }
    try {
      const finalReason = terminationReason === 'Other' ? `Other: ${customTerminationReason}` : terminationReason;
      await employeesApi.terminateWithDetails(emp.id, {
        reason: finalReason,
        termination_date: terminationDate,
        notes: terminationNotes,
      });
      await loadData();
      setShowTerminateConfirm(null);
      setTerminationReason('');
      setTerminationDate(format(new Date(), 'yyyy-MM-dd'));
      setTerminationNotes('');
      setCustomTerminationReason('');
    } catch (error) {
      console.error('Failed to terminate employee:', error);
      alert('Failed to terminate employee: ' + error.message);
    }
  };

  const handleResetPasscode = async () => {
    if (!showResetPasscode) return;
    if (!resetPasscodeValue || resetPasscodeValue.length < 4) {
      alert('Passcode must be at least 4 digits');
      return;
    }
    if (!/^\d+$/.test(resetPasscodeValue)) {
      alert('Passcode must contain only digits');
      return;
    }
    if (resetPasscodeValue !== resetPasscodeConfirm) {
      alert('Passcodes do not match');
      return;
    }
    setResettingPasscode(true);
    try {
      await employeesApi.resetPasscode(showResetPasscode.id, resetPasscodeValue, resetPasscodeReason);
      alert(`Passcode reset for ${showResetPasscode.name}. Please share the new passcode with the employee securely.`);
      setShowResetPasscode(null);
      setResetPasscodeValue('');
      setResetPasscodeConfirm('');
      setResetPasscodeReason('');
    } catch (error) {
      console.error('Failed to reset passcode:', error);
      alert('Failed to reset passcode: ' + error.message);
    } finally {
      setResettingPasscode(false);
    }
  };

  const handleReinstateEmployee = async (emp) => {
    if (!reinstatementReason.trim()) {
      alert('Please provide a reason for reinstatement');
      return;
    }
    try {
      await employeesApi.reinstate(emp.id, reinstatementReason);
      alert(`${emp.name} has been reinstated`);
      setShowReinstateModal(null);
      setReinstatementReason('');
      // Reload data and switch to active view
      await loadData();
      setEmployeeFilter('active');
    } catch (error) {
      console.error('Failed to reinstate employee:', error);
      alert('Failed to reinstate employee: ' + error.message);
    }
  };

  const handlePromoteEmployee = async (emp) => {
    if (!promotePassword || promotePassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    const email = promoteEmail || emp.email;
    if (!email) {
      alert('An email address is required');
      return;
    }
    try {
      const locs = promoteManagedLocations.length > 0 ? promoteManagedLocations : [emp.location_id];
      await employeesApi.promote(emp.id, {
        role: promoteRole,
        password: promotePassword,
        email,
        managed_locations: locs,
      });
      alert(`${emp.name} promoted to ${promoteRole === 'manager' ? 'Manager' : 'Assistant Manager'}`);
      setShowPromoteModal(null);
      setPromoteRole('manager');
      setPromotePassword('');
      setPromoteEmail('');
      setPromoteManagedLocations([]);
      await loadData();
    } catch (error) {
      alert('Failed to promote: ' + error.message);
    }
  };


  const handleTransferRequest = async (emp) => {
    if (!transferDestination) {
      alert('Please select a destination location');
      return;
    }
    try {
      await transfersApi.create({
        employee_id: emp.id,
        from_location_id: emp.location_id,
        to_location_id: transferDestination,
        reason: transferReason,
      });
      alert(`Transfer request created for ${emp.name}`);
      setShowTransferModal(null);
      setTransferDestination('');
      setTransferReason('');
      // If admin, switch to transfers view
      if (hasAdminRole) {
        setEmployeeFilter('transfers');
        await loadData();
      }
    } catch (error) {
      console.error('Failed to create transfer request:', error);
      alert('Failed to create transfer request: ' + error.message);
    }
  };

  const handleTransferReview = async (transfer, status) => {
    try {
      await transfersApi.review(transfer.id, { status, notes: '' });
      alert(`Transfer ${status}`);
      await loadData();
    } catch (error) {
      console.error('Failed to review transfer:', error);
      alert('Failed to review transfer: ' + error.message);
    }
  };

  const resetForm = () => {
    setNewEmployee({
      name: '',
      email: '',
      phone: '',
      position: '',
      hourly_rate: '',
      location_id: selectedLocation !== 'all' ? selectedLocation : '',
      password: 'emp123',
      date_of_birth: '',
      address: '',
      hire_date: '',
      start_date: '',
      notes: '',
      emergency_contact: { name: '', relationship: '', phone: '' },
    });
    setActiveTab('basic');
    setTerminationReason('');
    setTerminationDate(format(new Date(), 'yyyy-MM-dd'));
    setTerminationNotes('');
    setCustomTerminationReason('');
    setTransferDestination('');
    setTransferReason('');
    setReinstatementReason('');
  };

  const stats = [
    { title: 'Total Employees', value: employees.length, icon: Users, color: 'bg-purple-500' },
    { title: 'Active', value: employees.filter(e => e.status === 'active').length, icon: User, color: 'bg-violet-500' },
    { title: 'Avg Hourly Rate', value: `$${employees.length ? (employees.reduce((sum, e) => sum + (e.hourly_rate || 0), 0) / employees.length).toFixed(2) : '0.00'}`, icon: DollarSign, color: 'bg-indigo-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
          <button onClick={loadData} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700" title="Refresh" data-testid="refresh-employees-btn">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
          <p className="text-gray-500 mt-1">
            Manage your team members across{' '}
            {selectedLocation === 'all' ? 'all locations' : (locations || []).find(l => l.id === selectedLocation)?.name}
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Tabs & Search */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Active/Former/Transfers Tabs */}
            <div className="flex items-center gap-2">
              <Button
                variant={employeeFilter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEmployeeFilter('active')}
                className={employeeFilter === 'active' ? 'bg-purple-600' : ''}
              >
                <Users className="w-4 h-4 mr-2" />
                Active ({employees.length})
              </Button>
              <Button
                variant={employeeFilter === 'former' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEmployeeFilter('former')}
                className={employeeFilter === 'former' ? 'bg-gray-600' : ''}
              >
                <UserX className="w-4 h-4 mr-2" />
                Former ({formerEmployees.length})
              </Button>
              {hasAdminRole && (
                <Button
                  variant={employeeFilter === 'transfers' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEmployeeFilter('transfers')}
                  className={employeeFilter === 'transfers' ? 'bg-amber-600' : ''}
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfers ({transfers.filter(t => t.status === 'pending').length})
                </Button>
              )}
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                placeholder="Search by name, email, or employee ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="employee-search"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-0">
          <CardTitle className="text-lg">
            {employeeFilter === 'active' ? 'Active Employees' : 'Former Employees'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 pt-4">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Position</TableHead>
                {employeeFilter === 'active' ? (
                  <>
                    <TableHead>Phone</TableHead>
                    <TableHead>Hire Date</TableHead>
                    <TableHead>Rate</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead>Termination Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Terminated By</TableHead>
                  </>
                )}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.map((emp) => (
                <TableRow key={emp.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={emp.avatar} alt={emp.name} />
                        <AvatarFallback className="bg-slate-200">
                          {emp.name?.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900">{emp.name}</p>
                        <p className="text-sm text-slate-500">{emp.email}</p>
                        {emp.is_manager_employee && (
                          <Badge variant="outline" className="text-xs">Manager</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                      {emp.employee_number}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600">{emp.position}</TableCell>
                  {employeeFilter === 'active' ? (
                    <>
                      <TableCell className="text-slate-600">{emp.phone}</TableCell>
                      <TableCell className="text-slate-600">
                        {emp.hire_date ? format(parseISO(emp.hire_date), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {/* Hide manager pay rate from non-accounting_admin */}
                        {emp.is_manager_employee && !canViewManagerPay ? '—' : `$${emp.hourly_rate?.toFixed(2)}/hr`}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-slate-600">
                        {emp.termination_date ? format(parseISO(emp.termination_date), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell className="text-slate-600">{emp.termination_reason || '—'}</TableCell>
                      <TableCell className="text-slate-600">{emp.terminated_by_name || '—'}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <Badge className={emp.status === 'active' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}>
                      {emp.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setShowViewModal(emp)}>
                          <Eye className="w-4 h-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        {employeeFilter === 'active' ? (
                          <>
                            <DropdownMenuItem onClick={() => setEditingEmployee(emp)}>
                              <Edit2 className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowResetPasscode(emp)} className="text-purple-600" data-testid={`reset-passcode-${emp.id}`}>
                              <KeyRound className="w-4 h-4 mr-2" /> Reset Passcode
                            </DropdownMenuItem>
                            {/* Only show Transfer/Terminate for Manager or Admin, NOT Assistant Manager */}
                            {!isAssistantManagerOnly && (
                              <>
                                <DropdownMenuItem onClick={() => setShowTransferModal(emp)} className="text-blue-600">
                                  <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer
                                </DropdownMenuItem>
                                {hasAdminRole && !emp.linked_user_id && (
                                  <DropdownMenuItem onClick={() => { setShowPromoteModal(emp); setPromoteEmail(emp.email || ''); setPromoteManagedLocations([emp.location_id]); }} className="text-purple-600" data-testid={`promote-employee-${emp.id}`}>
                                    <ShieldCheck className="w-4 h-4 mr-2" /> Promote to Role
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setShowTerminateConfirm(emp)} className="text-amber-600">
                                  <UserX className="w-4 h-4 mr-2" /> Terminate
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Only show Reinstate for Manager or Admin, NOT Assistant Manager */}
                            {!isAssistantManagerOnly && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setShowReinstateModal(emp)} className="text-green-600">
                                  <RotateCcw className="w-4 h-4 mr-2" /> Reinstate
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredEmployees.length === 0 && employeeFilter !== 'transfers' && (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{employeeFilter === 'active' ? 'No active employees found' : 'No former employees'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfers Table (Admin only) */}
      {employeeFilter === 'transfers' && hasAdminRole && (
        <Card className="mb-6 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Transfer Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead>
                  <TableHead>From Location</TableHead>
                  <TableHead>To Location</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow key={transfer.id} className="hover:bg-slate-50">
                    <TableCell>
                      <p className="font-medium text-slate-900">{transfer.employee_name}</p>
                    </TableCell>
                    <TableCell className="text-slate-600">{transfer.from_location_name}</TableCell>
                    <TableCell className="text-slate-600">{transfer.to_location_name}</TableCell>
                    <TableCell className="text-slate-600">{transfer.requested_by_name}</TableCell>
                    <TableCell className="text-slate-600">
                      {transfer.requested_at ? format(parseISO(transfer.requested_at), 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        transfer.status === 'approved' ? 'bg-green-100 text-green-700' :
                        transfer.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {transfer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {transfer.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleTransferReview(transfer, 'approved')}
                          >
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleTransferReview(transfer, 'rejected')}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {transfer.status !== 'pending' && (
                        <span className="text-sm text-gray-500">
                          {transfer.reviewed_by_name && `by ${transfer.reviewed_by_name}`}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {transfers.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No transfer requests</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Terminate Confirmation Dialog */}
      <Dialog open={!!showTerminateConfirm} onOpenChange={() => {
        setShowTerminateConfirm(null);
        setTerminationReason('');
        setTerminationDate(format(new Date(), 'yyyy-MM-dd'));
        setTerminationNotes('');
        setCustomTerminationReason('');
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <UserX className="w-5 h-5" />
              Terminate Employee
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              You are terminating <strong>{showTerminateConfirm?.name}</strong>.
            </p>
            <div className="space-y-2">
              <Label htmlFor="termination-date" className="text-sm font-medium">
                Termination Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="termination-date"
                type="date"
                value={terminationDate}
                onChange={(e) => setTerminationDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="termination-reason" className="text-sm font-medium">
                Termination Reason <span className="text-red-500">*</span>
              </Label>
              <Select value={terminationReason} onValueChange={setTerminationReason}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resignation">Resignation</SelectItem>
                  <SelectItem value="Layoff">Layoff</SelectItem>
                  <SelectItem value="End of Contract">End of Contract</SelectItem>
                  <SelectItem value="Performance">Performance Issues</SelectItem>
                  <SelectItem value="Policy Violation">Policy Violation</SelectItem>
                  <SelectItem value="Retirement">Retirement</SelectItem>
                  <SelectItem value="Relocation">Relocation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Show custom reason field when "Other" is selected */}
            {terminationReason === 'Other' && (
              <div className="space-y-2">
                <Label htmlFor="custom-reason" className="text-sm font-medium">
                  Specify Reason <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="custom-reason"
                  placeholder="Enter custom termination reason..."
                  value={customTerminationReason}
                  onChange={(e) => setCustomTerminationReason(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="termination-notes" className="text-sm font-medium">
                Notes (optional)
              </Label>
              <Textarea
                id="termination-notes"
                placeholder="Additional notes about termination..."
                value={terminationNotes}
                onChange={(e) => setTerminationNotes(e.target.value)}
                rows={2}
              />
            </div>
            <p className="text-sm text-gray-500">
              This will end their employment and move them to Former Employees. Records will be preserved for compliance.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowTerminateConfirm(null);
              setTerminationReason('');
              setTerminationDate(format(new Date(), 'yyyy-MM-dd'));
              setTerminationNotes('');
              setCustomTerminationReason('');
            }}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={() => handleTerminateEmployee(showTerminateConfirm)}
              disabled={!terminationReason || (terminationReason === 'Other' && !customTerminationReason.trim())}
            >
              Terminate Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Employee Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
          </DialogHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="personal">Personal</TabsTrigger>
              <TabsTrigger value="emergency">Emergency</TabsTrigger>
            </TabsList>
            
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name</Label><Input value={newEmployee.name} onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Phone</Label><Input value={newEmployee.phone} onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})} /></div>
                  <div><Label>Position</Label><Input value={newEmployee.position} onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Hourly Rate ($)</Label><Input type="number" value={newEmployee.hourly_rate} onChange={(e) => setNewEmployee({...newEmployee, hourly_rate: e.target.value})} /></div>
                  <div>
                    <Label>Location</Label>
                    <Select value={newEmployee.location_id} onValueChange={(v) => setNewEmployee({...newEmployee, location_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        {(locations || []).map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'personal' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Hire Date</Label><Input type="date" value={newEmployee.hire_date} onChange={(e) => setNewEmployee({...newEmployee, hire_date: e.target.value})} /></div>
                  <div><Label>Date of Birth</Label><Input type="date" value={newEmployee.date_of_birth} onChange={(e) => setNewEmployee({...newEmployee, date_of_birth: e.target.value})} /></div>
                </div>
                <div><Label>Address</Label><Textarea value={newEmployee.address} onChange={(e) => setNewEmployee({...newEmployee, address: e.target.value})} /></div>
                <div><Label>Notes</Label><Textarea value={newEmployee.notes} onChange={(e) => setNewEmployee({...newEmployee, notes: e.target.value})} /></div>
              </div>
            )}
            
            {activeTab === 'emergency' && (
              <div className="space-y-4">
                <div><Label>Contact Name</Label><Input value={newEmployee.emergency_contact.name} onChange={(e) => setNewEmployee({...newEmployee, emergency_contact: {...newEmployee.emergency_contact, name: e.target.value}})} /></div>
                <div><Label>Relationship</Label><Input value={newEmployee.emergency_contact.relationship} onChange={(e) => setNewEmployee({...newEmployee, emergency_contact: {...newEmployee.emergency_contact, relationship: e.target.value}})} /></div>
                <div><Label>Phone</Label><Input value={newEmployee.emergency_contact.phone} onChange={(e) => setNewEmployee({...newEmployee, emergency_contact: {...newEmployee.emergency_contact, phone: e.target.value}})} /></div>
              </div>
            )}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddEmployee} className="bg-purple-600 hover:bg-purple-700" disabled={!newEmployee.name || !newEmployee.email || !newEmployee.location_id}>
              Add Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={!!editingEmployee} onOpenChange={() => setEditingEmployee(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          {editingEmployee && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={editingEmployee.name} onChange={(e) => setEditingEmployee({...editingEmployee, name: e.target.value})} /></div>
                <div><Label>Email</Label><Input type="email" value={editingEmployee.email} onChange={(e) => setEditingEmployee({...editingEmployee, email: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={editingEmployee.phone || ''} onChange={(e) => setEditingEmployee({...editingEmployee, phone: e.target.value})} /></div>
                <div><Label>Position</Label><Input value={editingEmployee.position} onChange={(e) => setEditingEmployee({...editingEmployee, position: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Hourly Rate ($)</Label><Input type="number" value={editingEmployee.hourly_rate} onChange={(e) => setEditingEmployee({...editingEmployee, hourly_rate: e.target.value})} /></div>
                <div><Label>Hire Date</Label><Input type="date" value={editingEmployee.hire_date || ''} onChange={(e) => setEditingEmployee({...editingEmployee, hire_date: e.target.value})} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEmployee(null)}>Cancel</Button>
            <Button onClick={handleUpdateEmployee} className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Employee Modal */}
      <Dialog open={!!showViewModal} onOpenChange={() => setShowViewModal(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {showViewModal && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={showViewModal.avatar} />
                  <AvatarFallback className="bg-purple-100 text-purple-700 text-lg">
                    {showViewModal.name?.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-semibold">{showViewModal.name}</h3>
                  <p className="text-slate-500">{showViewModal.position}</p>
                  <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{showViewModal.employee_number}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Email:</span> <span className="font-medium">{showViewModal.email}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{showViewModal.phone}</span></div>
                <div><span className="text-slate-500">Rate:</span> <span className="font-medium">${showViewModal.hourly_rate}/hr</span></div>
                <div><span className="text-gray-500">Status:</span> <Badge className="bg-purple-100 text-purple-700">{showViewModal.status}</Badge></div>
              </div>
              {showViewModal.emergency_contact && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Emergency Contact</p>
                  <p className="mt-2">{showViewModal.emergency_contact.name} ({showViewModal.emergency_contact.relationship})</p>
                  <p className="text-sm text-slate-600">{showViewModal.emergency_contact.phone}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Employee</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete {showDeleteConfirm?.name}? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteEmployee(showDeleteConfirm?.id)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Employee Modal */}
      <Dialog open={!!showTransferModal} onOpenChange={() => {
        setShowTransferModal(null);
        setTransferDestination('');
        setTransferReason('');
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <ArrowRightLeft className="w-5 h-5" />
              Transfer Employee
            </DialogTitle>
          </DialogHeader>
          {showTransferModal && (
            <div className="space-y-4 py-4">
              <p className="text-gray-600">
                Transfer <strong>{showTransferModal?.name}</strong> to a different location.
              </p>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Current Location</p>
                <p className="font-medium">
                  {(allLocations || []).find(l => l.id === showTransferModal?.location_id)?.name || 'Unknown'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Destination Location <span className="text-red-500">*</span></Label>
                <Select value={transferDestination} onValueChange={setTransferDestination}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select destination..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(allLocations || [])
                      .filter(l => l.id !== showTransferModal?.location_id)
                      .map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Textarea
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="Enter reason for transfer..."
                  rows={2}
                />
              </div>
              <p className="text-sm text-gray-500">
                This will create a transfer request that requires admin approval.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowTransferModal(null);
              setTransferDestination('');
              setTransferReason('');
            }}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => handleTransferRequest(showTransferModal)}
              disabled={!transferDestination}
            >
              Request Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Passcode Modal */}
      <Dialog open={!!showResetPasscode} onOpenChange={(o) => { if (!o) { setShowResetPasscode(null); setResetPasscodeValue(''); setResetPasscodeConfirm(''); setResetPasscodeReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-passcode-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <KeyRound className="w-5 h-5" />
              Reset Clock-In Passcode
            </DialogTitle>
          </DialogHeader>
          {showResetPasscode && (
            <div className="space-y-4 py-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                <p className="text-gray-700">
                  Reset the clock-in / portal passcode for <strong>{showResetPasscode.name}</strong>{' '}
                  (<span className="font-mono text-xs">#{showResetPasscode.employee_number}</span>).
                </p>
                <p className="text-xs text-purple-700 mt-1">
                  This updates the existing credentials only — it does not create a new employee record or change the employee ID.
                  Share the new passcode with the employee securely.
                </p>
              </div>
              <div>
                <Label>New Passcode (digits only, min 4)</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="new-password"
                  maxLength={10}
                  value={resetPasscodeValue}
                  onChange={(e) => setResetPasscodeValue(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g., 1234"
                  data-testid="reset-passcode-input"
                />
              </div>
              <div>
                <Label>Confirm Passcode</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="new-password"
                  maxLength={10}
                  value={resetPasscodeConfirm}
                  onChange={(e) => setResetPasscodeConfirm(e.target.value.replace(/\D/g, ''))}
                  placeholder="Re-enter passcode"
                  data-testid="reset-passcode-confirm-input"
                />
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={resetPasscodeReason}
                  onChange={(e) => setResetPasscodeReason(e.target.value)}
                  placeholder="e.g., Employee forgot passcode"
                  data-testid="reset-passcode-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasscode(null)} disabled={resettingPasscode}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleResetPasscode}
              disabled={resettingPasscode || !resetPasscodeValue || resetPasscodeValue !== resetPasscodeConfirm}
              data-testid="reset-passcode-submit-btn"
            >
              {resettingPasscode && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Reset Passcode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Employee Modal */}
      <Dialog open={!!showReinstateModal} onOpenChange={() => {
        setShowReinstateModal(null);
        setReinstatementReason('');
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <RotateCcw className="w-5 h-5" />
              Reinstate Employee
            </DialogTitle>
          </DialogHeader>
          {showReinstateModal && (
            <div className="space-y-4 py-4">
              <p className="text-gray-600">
                You are reinstating <strong>{showReinstateModal?.name}</strong> as an active employee.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reinstatement-reason" className="text-sm font-medium">
                  Reason for Reinstatement <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="reinstatement-reason"
                  placeholder="Enter reason for reinstating this employee..."
                  value={reinstatementReason}
                  onChange={(e) => setReinstatementReason(e.target.value)}
                  rows={3}
                  data-testid="reinstatement-reason-input"
                />
              </div>
              <p className="text-sm text-gray-500">
                This will restore the employee to active status and they will appear in the Active employees list.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowReinstateModal(null);
              setReinstatementReason('');
            }}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReinstateEmployee(showReinstateModal)}
              disabled={!reinstatementReason.trim()}
              data-testid="confirm-reinstate-btn"
            >
              Reinstate Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Employee Modal */}
      <Dialog open={!!showPromoteModal} onOpenChange={() => setShowPromoteModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              Promote {showPromoteModal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">This will create a login account for this employee, allowing them to access the management portal. Their existing history (time entries, schedules) will be preserved.</p>
            <div>
              <Label>Role</Label>
              <Select value={promoteRole} onValueChange={setPromoteRole}>
                <SelectTrigger data-testid="promote-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="assistant_manager">Assistant Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email (for login)</Label>
              <Input
                type="email"
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                placeholder="user@example.com"
                data-testid="promote-email-input"
              />
            </div>
            <div>
              <Label>Password (for login)</Label>
              <Input
                type="password"
                value={promotePassword}
                onChange={(e) => setPromotePassword(e.target.value)}
                placeholder="Min 6 characters"
                data-testid="promote-password-input"
              />
            </div>
            <div>
              <Label>Managed Locations</Label>
              <div className="space-y-2 mt-1">
                {allLocations.map((loc) => (
                  <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={promoteManagedLocations.includes(loc.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPromoteManagedLocations((prev) => [...prev, loc.id]);
                        } else {
                          setPromoteManagedLocations((prev) => prev.filter((l) => l !== loc.id));
                        }
                      }}
                      className="rounded"
                    />
                    {loc.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteModal(null)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => handlePromoteEmployee(showPromoteModal)}
              disabled={!promotePassword || promotePassword.length < 6}
              data-testid="promote-confirm-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" /> Promote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
